/**
 * useHotspotRewards — W1.3
 *
 * Holt Reward-Historie vom Backend (GET /api/v2/hotspots/:id/rewards).
 * Lokal typisiert bis B1.2 in api-types landet.
 */
import { useQuery } from '@tanstack/react-query'
import type { components } from '@heliumtracker/api-types'
import { useApi } from '../../lib/use-api'
import apiClient from '../../lib/api-client'

// --- Lokale Typen ---

export type RewardEra = 'post' | 'pre'

export type RewardEntry = {
  date: string
  hnt: number | null
  iot: number | null
  mobile: number | null
  era: RewardEra
}

export type RewardsResponse = {
  data: RewardEntry[]
  meta: {
    pagy: {
      page: number
      pages: number
      count: number
      items: number
    }
  }
}

type UseHotspotRewardsResult = {
  data: RewardEntry[] | undefined
  meta: RewardsResponse['meta'] | undefined
  isLoading: boolean
  isError: boolean
  refetch: () => void
}

export type ChartRewardEntry = {
  date: string
  hnt: number
  iot: number
  mobile: number
  // Affiliate existiert nicht als Tagesdaten (nur %-Anteil auf UserHotspot) — kein Feld hier
}

// --- Hook ---

// New Overload
export function useHotspotRewards(
  id: string,
  options: { from?: string; to?: string }
): ReturnType<typeof useQuery<ChartRewardEntry[]>>;

// Legacy Overload
export function useHotspotRewards(
  id: string,
  era?: RewardEra,
  from?: string,
  to?: string,
  items?: number
): UseHotspotRewardsResult;

export function useHotspotRewards(
  id: string,
  optionsOrEra?: RewardEra | { from?: string; to?: string },
  fromArg?: string,
  toArg?: string,
  itemsArg = 30
): unknown {
  const isObject = typeof optionsOrEra === 'object' && optionsOrEra !== null;

  const era = isObject ? undefined : (optionsOrEra as RewardEra | undefined);
  const from = isObject ? (optionsOrEra as { from?: string }).from : fromArg;
  const to = isObject ? (optionsOrEra as { to?: string }).to : toArg;
  const items = isObject ? undefined : itemsArg;

  const legacyQuery = useApi<RewardsResponse>(
    ['hotspot-rewards', id, era ?? 'all', from, to],
    async () => {
      // TypeScript compiler complains about dynamic endpoints when indexing
      // so we have to circumvent it cleanly
      const client = apiClient as unknown as { GET: (path: string, options: unknown) => Promise<unknown> }
      const result = await client.GET('/api/v2/hotspots/{id}/rewards', {
        params: {
          path: { id },
          query: {
            ...(era ? { era } : {}),
            ...(from ? { from } : {}),
            ...(to ? { to } : {}),
            items,
          },
        },
      })
      return result as { data?: RewardsResponse; error?: unknown }
    }
  )

  const newQuery = useQuery({
    queryKey: ['hotspot-rewards-chart', id, from, to],
    queryFn: async () => {
      // from/to sind optional: ohne Angabe liefert das Backend die gesamte
      // Historie (Preset "Alle"). Kein Default-Fenster mehr erzwingen.
      const client = apiClient as unknown as { GET: (path: string, options: unknown) => Promise<unknown> }
      const response = await client.GET('/api/v2/hotspots/{hotspot_id}/rewards', {
        params: {
          path: { hotspot_id: Number(id) },
          query: {
            ...(from ? { from } : {}),
            ...(to ? { to } : {}),
            per_page: 100
          }
        }
      })
      const result = response as { data?: { data: components['schemas']['Reward'][] }; error?: unknown }

      if (result.error) throw new Error('Failed to fetch rewards')

      const rewards = result.data?.data || []
      const mapped: ChartRewardEntry[] = rewards.map((r: components['schemas']['Reward']) => {
        const raw = r as unknown as { date?: string; hnt?: number; iot?: number; mobile?: number }
        return {
          date: raw.date ?? (r.timestamp ? r.timestamp.split('T')[0] : ''),
          hnt: raw.hnt ?? (r.token_type === 'hnt' ? r.amount ?? 0 : 0),
          iot: raw.iot ?? (r.token_type === 'iot' ? r.amount ?? 0 : 0),
          mobile: raw.mobile ?? (r.token_type === 'mobile' ? r.amount ?? 0 : 0),
        }
      })

      return mapped
    },
    enabled: isObject
  })

  if (isObject) {
    return newQuery
  }

  return {
    data: legacyQuery.data?.data,
    meta: legacyQuery.data?.meta,
    isLoading: legacyQuery.isLoading,
    isError: legacyQuery.isError,
    refetch: () => legacyQuery.refetch(),
  }
}
